import { afterNextRender, Component, DestroyRef, ElementRef, inject, Injector, NgZone, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { InterviewService } from '@App';

// Floating "New required/optional field" alert. Place it as the first child of the element that holds the
// questions; it shows when processing reveals a question the user has already scrolled past.
@Component({
    selector: 'interview-new-field-alert',
    imports: [MatIconModule],
    host: { 'aria-live': 'polite' },
    template: `
        @if (alert(); as alert) {
            <button type="button" class="new-question-alert" id="new-question-alert" (click)="goToQuestion()">
                <mat-icon aria-hidden="true" svgIcon="arrow-up"></mat-icon>
                <span>New {{ alert.required ? 'required' : 'optional' }} field</span>
            </button>
        }
    `,
    styles: [`
        :host {
            position: sticky;
            top: 0;
            height: 0;
            z-index: 20;
            display: flex;
            justify-content: center;
            /* The host is zero-height so it doesn't push content; stretching would squash the button to 0. */
            align-items: flex-start;
            overflow: visible;
        }

        .new-question-alert {
            display: inline-flex;
            align-items: center;
            gap: 1.75rem;
            white-space: nowrap;
            line-height: 1.2;
            margin-top: 0.75rem;
            padding: 1.25rem 2rem;
            border: 0;
            border-radius: 12px;
            background: #9dc99a;
            color: #1f2d1f;
            font-family: Roboto, sans-serif;
            font-size: 1rem;
            font-weight: 400;
            text-transform: none;
            letter-spacing: normal;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            cursor: pointer;
        }

        .new-question-alert mat-icon {
            flex-shrink: 0;
            width: 28px;
            height: 28px;
            color: #2b3a4a;
        }
    `]
})
export class InterviewNewFieldAlertComponent implements OnInit {
    private readonly interviewService = inject(InterviewService);
    private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
    private readonly injector = inject(Injector);
    private readonly zone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);

    // A signal so setting it from a render hook (eg. during the ApplicationRef.tick() Next uses before validating)
    // re-renders in that same tick instead of failing the dev-mode ExpressionChangedAfterItHasBeenChecked check.
    readonly alert = signal<{ questionId: string; required: boolean }>(null);

    ngOnInit() {
        this.interviewService.newlyVisibleQuestion$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(alert => {
            // Checked after the next render, once the new question is in the DOM.
            afterNextRender(() => {
                if (this.isAboveViewport(alert.questionId)) {
                    this.alert.set(alert);
                }
            }, { injector: this.injector });
        });

        // Capture phase so it hears whichever ancestor (or the page) is scrolling.
        const onScroll = () => {
            if (this.alert() && !this.isAboveViewport(this.alert().questionId)) {
                this.zone.run(() => this.alert.set(null));
            }
        };
        this.zone.runOutsideAngular(() => document.addEventListener('scroll', onScroll, true));
        this.destroyRef.onDestroy(() => document.removeEventListener('scroll', onScroll, true));
    }

    dismiss() {
        this.alert.set(null);
    }

    goToQuestion() {
        document.getElementById(this.alert()?.questionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.alert.set(null);
    }

    // True when the question is in this alert's container and sits above the visible part of it.
    private isAboveViewport(questionId: string): boolean {
        const container = this.host.nativeElement.parentElement;
        const element = document.getElementById(questionId);
        if (!container || !element || !container.contains(element)) {
            return false;
        }
        return element.getBoundingClientRect().top < Math.max(this.getScrollParent(container).getBoundingClientRect().top, 0);
    }

    private getScrollParent(element: HTMLElement): HTMLElement {
        for (let el = element; el; el = el.parentElement) {
            if (/(auto|scroll)/.test(getComputedStyle(el).overflowY) && el.scrollHeight > el.clientHeight) {
                return el;
            }
        }
        return document.documentElement;
    }
}
